/**
 * Rebuy Popup for Poker Reborn
 *
 * Displays when player loses all chips, allowing them to rebuy.
 * Includes countdown timer and auto-refill/auto-rebuy checkboxes.
 *
 * @see docs/screens/room/popups/REBUY_POPUP.md - Visual specs, behavior
 * @see BuyInPopup - Sister component sharing slider, checkboxes, loading state pattern
 * @see ../../../../poker_platform/docs/protocols/POKERTALK.md - Protocol (Rebuy section)
 */

import { Container, Graphics, Text, Ticker } from 'pixi.js';
import { Button, PRIMARY_BUTTON_STYLE, SECONDARY_BUTTON_STYLE } from '@/components/common/Button';
import { audioService } from '@/services/AudioService';
import {
  CHIP_INPUT_GAPS,
  SLIDER_LAYOUT,
  CHECKBOX_LAYOUT,
  BUTTON_LAYOUT,
  POPUP_LAYOUT,
} from './chipInputLayout';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Overlay */
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },

  /** Popup (dimensions calculated dynamically via calculatePopupSize) */
  popup: {
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 16,
  },

  /** Title */
  title: {
    fill: 0xffffff,
    fontSize: 24,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Timer */
  timer: {
    fontSize: 19,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
    colorNormal: 0xffffff,
    colorWarning: 0xffd700,
    colorCritical: 0xff4444,
    warningThreshold: 15,
    criticalThreshold: 5,
  },

  /** Message text */
  message: {
    fill: 0xcccccc,
    fontSize: 16,
    fontFamily: 'Roboto, Arial, sans-serif',
  },

  /** Amount text */
  amountText: {
    fill: 0xffd700,
    fontSize: 36,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Layout Y positions (relative to center) - popup-specific only */
  layout: {
    title: -170,
    timer: -137,
    message: -103,
    amount: -55,
    // Shared section uses CHIP_INPUT_GAPS for consistent spacing
  },
};

// =============================================================================
// REBUY POPUP
// =============================================================================

export interface RebuyPopupOptions {
  /** Minimum rebuy amount */
  minBuyIn: number;

  /** Maximum rebuy amount */
  maxBuyIn: number;

  /** Poor timeout in seconds */
  timeout: number;

  /** When rebuy was triggered */
  at: 'end' | 'start';

  /** Stakes for display */
  stakes: { smallBlind: number; bigBlind: number };
}

export class RebuyPopup extends Container {
  private overlay: Graphics;
  private popup: Container;
  private background: Graphics;
  private titleText: Text;
  private timerText: Text;
  private messageText: Text;
  private amountText: Text;
  private sliderTrack: Graphics;
  private sliderFill: Graphics;
  private sliderHandle: Graphics;
  private sliderMinLabel: Text;
  private sliderMaxLabel: Text;
  private autoRebuyCheckbox: Container;
  private autoRebuyBox: Graphics;
  private autoRefillCheckbox: Container;
  private autoRefillBox: Graphics;
  private rebuyButton: Button;
  private leaveButton: Button;

  private minBuyIn: number;
  private maxBuyIn: number;
  private tableMaxBuyIn: number; // Original max from table (before chip limit)
  private currentAmount: number;
  private timeRemaining: number;
  private screenWidth: number;
  private screenHeight: number;
  private popupWidth: number;
  private popupHeight: number;
  private contentTopY: number;
  private contentBottomY: number;
  private isDragging = false;
  private timerInterval: number | null = null;
  private pulsePhase = 0;
  private loading = true; // Start in loading state
  private autoRefill = true; // Checkbox state (default ON)
  private autoRebuy = true; // Checkbox state (default ON)

  /** Callback when rebuy confirmed */
  onConfirm: ((amount: number, autoRefill: boolean, autoRebuy: boolean) => void) | null = null;

  /** Callback when player leaves */
  onLeave: (() => void) | null = null;

  /** Callback when timer expires */
  onTimeout: (() => void) | null = null;

  /** Callback when loading but player has insufficient chips */
  onInsufficientChips: ((minRequired: number, playerChips: number) => void) | null = null;

  constructor(
    screenWidth: number,
    screenHeight: number,
    options: RebuyPopupOptions
  ) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.minBuyIn = options.minBuyIn;
    this.maxBuyIn = options.maxBuyIn;
    this.tableMaxBuyIn = options.maxBuyIn; // Store original max
    this.currentAmount = Math.floor(options.minBuyIn + (options.maxBuyIn - options.minBuyIn) * 0.75);
    this.timeRemaining = options.timeout;

    // Store content bounds for background positioning
    this.contentTopY = STYLES.layout.title; // -170
    this.contentBottomY =
      STYLES.layout.amount +
      CHIP_INPUT_GAPS.amountToSlider +
      CHIP_INPUT_GAPS.sliderToLabels +
      CHIP_INPUT_GAPS.labelsToCheckboxes +
      CHIP_INPUT_GAPS.checkboxesToButtons +
      BUTTON_LAYOUT.height; // +134

    // Calculate popup dimensions from content bounds
    this.popupWidth = SLIDER_LAYOUT.width + POPUP_LAYOUT.sidePadding * 2;
    this.popupHeight =
      this.contentBottomY - this.contentTopY + POPUP_LAYOUT.verticalPadding * 2;

    // Play sound
    audioService.play('popup_open');

    // Create overlay
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    // Block overlay clicks (no close on overlay for rebuy - too important)
    this.overlay.on('pointerdown', (e) => { e.stopPropagation(); });
    this.addChild(this.overlay);

    // Create popup container
    this.popup = new Container();
    this.popup.x = screenWidth / 2;
    this.popup.y = screenHeight / 2;
    this.addChild(this.popup);

    // Create background
    this.background = new Graphics();
    this.popup.addChild(this.background);
    this.drawBackground();

    // Stop popup clicks from closing
    this.background.eventMode = 'static';
    this.background.on('pointerdown', (e) => { e.stopPropagation(); });

    // Create title
    this.titleText = new Text({
      text: 'Rebuy',
      style: STYLES.title,
    });
    this.titleText.anchor.set(0.5);
    this.titleText.y = STYLES.layout.title;
    this.popup.addChild(this.titleText);

    // Create timer
    this.timerText = new Text({
      text: this.formatTime(this.timeRemaining),
      style: {
        ...STYLES.timer,
        fill: STYLES.timer.colorNormal,
      },
    });
    this.timerText.anchor.set(0.5);
    this.timerText.y = STYLES.layout.timer;
    this.popup.addChild(this.timerText);

    // Create message
    this.messageText = new Text({
      text: "You're out of chips!\nRebuy to continue playing.",
      style: {
        ...STYLES.message,
        align: 'center',
      },
    });
    this.messageText.anchor.set(0.5);
    this.messageText.y = STYLES.layout.message;
    this.popup.addChild(this.messageText);

    // Create amount display (shows "Loading..." until balance received)
    this.amountText = new Text({
      text: 'Loading...',
      style: { ...STYLES.amountText, fill: 0x888899 }, // Dimmed while loading
    });
    this.amountText.anchor.set(0.5);
    this.amountText.y = STYLES.layout.amount;
    this.popup.addChild(this.amountText);

    // Create slider
    this.sliderTrack = new Graphics();
    this.sliderFill = new Graphics();
    this.sliderHandle = new Graphics();
    this.popup.addChild(this.sliderTrack);
    this.popup.addChild(this.sliderFill);
    this.popup.addChild(this.sliderHandle);
    this.drawSlider();
    this.setupSliderInteraction();

    // Calculate shared section positions using CHIP_INPUT_GAPS
    const amountY = STYLES.layout.amount;
    const sliderY = amountY + CHIP_INPUT_GAPS.amountToSlider;
    const sliderLabelsY = sliderY + CHIP_INPUT_GAPS.sliderToLabels;
    const checkboxY = sliderLabelsY + CHIP_INPUT_GAPS.labelsToCheckboxes;
    const buttonY = checkboxY + CHIP_INPUT_GAPS.checkboxesToButtons;

    // Create slider min/max labels
    const sliderWidth = SLIDER_LAYOUT.width;
    const labelStyle = { ...STYLES.message, fontSize: 12 };

    this.sliderMinLabel = new Text({
      text: `$${options.minBuyIn}`,
      style: labelStyle,
    });
    this.sliderMinLabel.anchor.set(0.5);
    this.sliderMinLabel.x = -sliderWidth / 2;
    this.sliderMinLabel.y = sliderLabelsY;
    this.popup.addChild(this.sliderMinLabel);

    this.sliderMaxLabel = new Text({
      text: `$${options.maxBuyIn}`,
      style: labelStyle,
    });
    this.sliderMaxLabel.anchor.set(0.5);
    this.sliderMaxLabel.x = sliderWidth / 2;
    this.sliderMaxLabel.y = sliderLabelsY;
    this.popup.addChild(this.sliderMaxLabel);

    // Disable slider interaction while loading
    this.sliderHandle.eventMode = 'none';
    this.sliderTrack.eventMode = 'none';

    // Create checkboxes (arranged horizontally, centered - same as BuyInPopup)
    const { gap: checkboxGap, itemWidth: checkboxWidth } = CHECKBOX_LAYOUT;
    const totalCheckboxWidth = checkboxWidth * 2 + checkboxGap;
    const checkboxStartX = -totalCheckboxWidth / 2;

    // Auto Rebuy checkbox (left)
    this.autoRebuyCheckbox = new Container();
    this.autoRebuyCheckbox.x = checkboxStartX;
    this.autoRebuyCheckbox.y = checkboxY;
    this.autoRebuyBox = this.createCheckbox('Auto Rebuy', () => {
      this.autoRebuy = !this.autoRebuy;
      this.updateCheckbox(this.autoRebuyBox, this.autoRebuy);
    });
    this.autoRebuyCheckbox.addChild(this.autoRebuyBox);
    this.popup.addChild(this.autoRebuyCheckbox);
    this.updateCheckbox(this.autoRebuyBox, this.autoRebuy); // Set initial checked state

    // Auto Refill checkbox (right)
    this.autoRefillCheckbox = new Container();
    this.autoRefillCheckbox.x = checkboxStartX + checkboxWidth + checkboxGap;
    this.autoRefillCheckbox.y = checkboxY;
    this.autoRefillBox = this.createCheckbox('Auto Refill', () => {
      this.autoRefill = !this.autoRefill;
      this.updateCheckbox(this.autoRefillBox, this.autoRefill);
    });
    this.autoRefillCheckbox.addChild(this.autoRefillBox);
    this.popup.addChild(this.autoRefillCheckbox);
    this.updateCheckbox(this.autoRefillBox, this.autoRefill); // Set initial checked state

    // Create action buttons
    const { width: buttonWidth, height: buttonHeight, gap: buttonGap } = BUTTON_LAYOUT;
    const totalButtonsWidth = buttonWidth * 2 + buttonGap;

    // Create leave button (left)
    this.leaveButton = new Button({
      label: 'Leave Table',
      width: buttonWidth,
      height: buttonHeight,
      style: SECONDARY_BUTTON_STYLE,
    });
    this.leaveButton.onClick = () => { this.handleLeave(); };
    this.leaveButton.x = -totalButtonsWidth / 2;
    this.leaveButton.y = buttonY;
    this.popup.addChild(this.leaveButton);

    // Create rebuy button (right) - disabled until balance loaded
    this.rebuyButton = new Button({
      label: 'Rebuy',
      width: buttonWidth,
      height: buttonHeight,
      style: PRIMARY_BUTTON_STYLE,
    });
    this.rebuyButton.onClick = () => { this.handleConfirm(); };
    this.rebuyButton.x = -totalButtonsWidth / 2 + buttonWidth + buttonGap;
    this.rebuyButton.y = buttonY;
    this.rebuyButton.disabled = true; // Disabled until balance loaded
    this.popup.addChild(this.rebuyButton);

    // Start timer
    this.startTimer();
  }

  /**
   * Get current rebuy amount
   */
  getAmount(): number {
    return this.currentAmount;
  }

  /**
   * Set player's chip balance and enable controls.
   * Called when account balance is received from server.
   * Returns false if player has insufficient chips.
   */
  setBalance(playerChips: number): boolean {
    // Calculate effective max buy-in (limited by player's chips)
    const effectiveMaxBuyIn = Math.min(this.tableMaxBuyIn, playerChips);

    // Check if player has enough chips for minimum buy-in
    if (effectiveMaxBuyIn < this.minBuyIn) {
      if (this.onInsufficientChips) {
        this.onInsufficientChips(this.minBuyIn, playerChips);
      }
      return false;
    }

    // Update max buy-in
    this.maxBuyIn = effectiveMaxBuyIn;

    // Calculate initial amount (75% of range)
    this.currentAmount = Math.floor(this.minBuyIn + (this.maxBuyIn - this.minBuyIn) * 0.75);

    // Update amount display with gold color
    this.amountText.text = this.formatAmount(this.currentAmount);
    this.amountText.style.fill = STYLES.amountText.fill;

    // Update slider max label
    this.sliderMaxLabel.text = `$${this.maxBuyIn}`;

    // Redraw slider with correct values
    this.drawSlider();

    // Enable slider interaction
    this.sliderHandle.eventMode = 'static';
    this.sliderHandle.cursor = 'grab';
    this.sliderTrack.eventMode = 'static';
    this.sliderTrack.cursor = 'pointer';

    // Enable rebuy button
    this.rebuyButton.disabled = false;

    // Mark as no longer loading
    this.loading = false;

    return true;
  }

  /**
   * Check if popup is still loading balance
   */
  isLoading(): boolean {
    return this.loading;
  }

  /**
   * Resize popup
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Resize overlay
    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    // Reposition popup
    this.popup.x = width / 2;
    this.popup.y = height / 2;
  }

  /**
   * Clean up on destroy
   */
  destroy(): void {
    this.stopTimer();
    super.destroy({ children: true });
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private drawBackground(): void {
    const { fill, stroke, cornerRadius } = STYLES.popup;
    const width = this.popupWidth;
    const height = this.popupHeight;

    // Position background relative to content bounds (not centered at Y=0)
    const topEdge = this.contentTopY - POPUP_LAYOUT.verticalPadding;
    const leftEdge = -width / 2;

    this.background.clear();
    this.background.roundRect(leftEdge, topEdge, width, height, cornerRadius);
    this.background.fill(fill);
    this.background.setStrokeStyle({ width: 2, color: stroke });
    this.background.stroke();
  }

  private drawSlider(): void {
    const { width, height, trackColor, fillColor, handleSize, handleColor } = SLIDER_LAYOUT;
    const sliderY = STYLES.layout.amount + CHIP_INPUT_GAPS.amountToSlider;

    // Calculate fill percentage
    const percentage =
      (this.currentAmount - this.minBuyIn) / (this.maxBuyIn - this.minBuyIn);
    const fillWidth = width * percentage;
    const handleX = -width / 2 + fillWidth;

    // Track
    this.sliderTrack.clear();
    this.sliderTrack.roundRect(-width / 2, sliderY - height / 2, width, height, height / 2);
    this.sliderTrack.fill(trackColor);

    // Fill
    this.sliderFill.clear();
    this.sliderFill.roundRect(-width / 2, sliderY - height / 2, fillWidth, height, height / 2);
    this.sliderFill.fill(fillColor);

    // Handle
    this.sliderHandle.clear();
    this.sliderHandle.circle(handleX, sliderY, handleSize / 2);
    this.sliderHandle.fill(handleColor);
    this.sliderHandle.setStrokeStyle({ width: 2, color: 0x333333 });
    this.sliderHandle.stroke();
  }

  private createCheckbox(label: string, onClick: () => void): Graphics {
    const { size, uncheckedColor, labelColor, fontSize } = CHECKBOX_LAYOUT;
    const container = new Graphics();

    // Draw checkbox box (positioned at origin)
    container.roundRect(0, -size / 2, size, size, 4);
    container.fill(uncheckedColor);
    container.setStrokeStyle({ width: 2, color: 0x5a5a7e });
    container.stroke();

    // Add label
    const labelText = new Text({
      text: label,
      style: {
        fill: labelColor,
        fontSize,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    labelText.x = size + 8;
    labelText.anchor.set(0, 0.5);
    container.addChild(labelText);

    // Make interactive
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.hitArea = {
      contains: (x: number, y: number) => {
        return x >= 0 && x <= size + 100 && y >= -size / 2 && y <= size / 2;
      },
    };
    container.on('pointerdown', onClick);

    return container;
  }

  private updateCheckbox(checkbox: Graphics, checked: boolean): void {
    const { size, uncheckedColor, checkedColor, checkmarkColor } = CHECKBOX_LAYOUT;

    // Clear and redraw
    checkbox.clear();
    checkbox.roundRect(0, -size / 2, size, size, 4);
    checkbox.fill(checked ? checkedColor : uncheckedColor);
    checkbox.setStrokeStyle({ width: 2, color: checked ? 0x6abf6a : 0x5a5a7e });
    checkbox.stroke();

    // Draw checkmark if checked
    if (checked) {
      checkbox.setStrokeStyle({ width: 3, color: checkmarkColor });
      checkbox.moveTo(4, 0);
      checkbox.lineTo(size / 3, size / 2 - 5);
      checkbox.lineTo(size - 4, -size / 2 + 5);
      checkbox.stroke();
    }
  }

  private setupSliderInteraction(): void {
    const sliderWidth = SLIDER_LAYOUT.width;

    // Make handle interactive
    this.sliderHandle.eventMode = 'static';
    this.sliderHandle.cursor = 'grab';

    this.sliderHandle.on('pointerdown', () => {
      this.isDragging = true;
      this.sliderHandle.cursor = 'grabbing';
    });

    // Make track interactive for click-to-position
    this.sliderTrack.eventMode = 'static';
    this.sliderTrack.cursor = 'pointer';

    this.sliderTrack.on('pointerdown', (e) => {
      const localPos = e.getLocalPosition(this.popup);
      this.updateSliderFromPosition(localPos.x, sliderWidth);
    });

    // Global pointer move and up
    this.popup.eventMode = 'static';

    this.popup.on('pointermove', (e) => {
      if (this.isDragging) {
        const localPos = e.getLocalPosition(this.popup);
        this.updateSliderFromPosition(localPos.x, sliderWidth);
      }
    });

    this.popup.on('pointerup', () => {
      this.isDragging = false;
      this.sliderHandle.cursor = 'grab';
    });

    this.popup.on('pointerupoutside', () => {
      this.isDragging = false;
      this.sliderHandle.cursor = 'grab';
    });
  }

  private updateSliderFromPosition(x: number, sliderWidth: number): void {
    // Clamp x to slider bounds
    const clampedX = Math.max(-sliderWidth / 2, Math.min(sliderWidth / 2, x));

    // Calculate percentage
    const percentage = (clampedX + sliderWidth / 2) / sliderWidth;

    // Calculate amount
    this.currentAmount = Math.round(
      this.minBuyIn + (this.maxBuyIn - this.minBuyIn) * percentage
    );

    // Update display
    this.amountText.text = this.formatAmount(this.currentAmount);
    this.drawSlider();
  }

  private startTimer(): void {
    this.timerInterval = window.setInterval(() => {
      this.timeRemaining--;
      this.updateTimerDisplay();

      if (this.timeRemaining <= 0) {
        this.stopTimer();
        this.handleTimeout();
      }
    }, 1000);

    // Start pulse animation via ticker
    Ticker.shared.add(this.updatePulse, this);
  }

  private stopTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    Ticker.shared.remove(this.updatePulse, this);
  }

  private updateTimerDisplay(): void {
    this.timerText.text = this.formatTime(this.timeRemaining);

    // Update color based on time remaining
    let color: number;
    if (this.timeRemaining <= STYLES.timer.criticalThreshold) {
      color = STYLES.timer.colorCritical;
    } else if (this.timeRemaining <= STYLES.timer.warningThreshold) {
      color = STYLES.timer.colorWarning;
    } else {
      color = STYLES.timer.colorNormal;
    }
    this.timerText.style.fill = color;

    // Play warning sounds
    if (this.timeRemaining === STYLES.timer.warningThreshold) {
      audioService.play('tick');
    } else if (this.timeRemaining === STYLES.timer.criticalThreshold) {
      audioService.play('act');
    }
  }

  private updatePulse = (): void => {
    this.pulsePhase += 0.1;

    // Apply pulse only in warning/critical states
    if (this.timeRemaining <= STYLES.timer.warningThreshold) {
      const pulseSpeed = this.timeRemaining <= STYLES.timer.criticalThreshold ? 2 : 1;
      const scale = 1 + 0.05 * Math.sin(this.pulsePhase * pulseSpeed);
      this.timerText.scale.set(scale);
    } else {
      this.timerText.scale.set(1);
    }
  };

  private handleConfirm(): void {
    this.stopTimer();
    audioService.play('button_click');

    if (this.onConfirm) {
      this.onConfirm(this.currentAmount, this.autoRefill, this.autoRebuy);
    }
    this.destroy();
  }

  private handleLeave(): void {
    this.stopTimer();
    audioService.play('button_click');

    if (this.onLeave) {
      this.onLeave();
    }
    this.destroy();
  }

  private handleTimeout(): void {
    audioService.play('second_act'); // Urgent timeout alert

    if (this.onTimeout) {
      this.onTimeout();
    }
    this.destroy();
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private formatAmount(amount: number): string {
    return `$${amount.toLocaleString()}`;
  }
}
